/**
 * Pure, deterministic serializers — one per export format. Each takes the list
 * of displayed colors (hex strings) and returns copy-paste-ready text in its
 * target format. Empty input yields a valid-but-empty document, never malformed
 * output. The `EXPORT_FORMATS` registry is the single source of truth shared by
 * `ExportTabs` and the unit tests, so the UI and the tests can never drift.
 *
 * Colors are named by `nameColors` (Tailwind-style `blue-500`, `slate-200`) so
 * exports read as semantic tokens rather than `color-1`, `color-2`.
 */
import { nameColors } from './naming';

/** CSS custom properties under :root. */
export const toCssVars = (colors: string[]): string => {
  const names = nameColors(colors);
  return `:root {\n${colors.map((c, i) => `  --${names[i]}: ${c};`).join('\n')}\n}`;
};

/** A Tailwind config block extending the color palette. Valid JS module. */
export const toTailwind = (colors: string[]): string => {
  const names = nameColors(colors);
  const entries = colors.map((c, i) => `        '${names[i]}': '${c}',`).join('\n');
  const body = entries ? `\n${entries}\n      ` : '';
  return (
    `module.exports = {\n` +
    `  theme: {\n` +
    `    extend: {\n` +
    `      colors: {${body}},\n` +
    `    },\n` +
    `  },\n` +
    `};`
  );
};

/** W3C design-token shape (Figma Tokens / Style Dictionary compatible). */
export const toFigmaTokens = (colors: string[]): string => {
  const names = nameColors(colors);
  return JSON.stringify(
    Object.fromEntries(colors.map((c, i) => [names[i], { $type: 'color', $value: c }])),
    null,
    2,
  );
};

/** Raw JSON list of hex colors. */
export const toRawJson = (colors: string[]): string => JSON.stringify({ colors }, null, 2);

export type ExportFormatId = 'css' | 'tailwind' | 'figma' | 'json';

export interface ExportFormat {
  id: ExportFormatId;
  label: string;
  /** Hint for the rendered code block (and future syntax highlighting). */
  language: string;
  /** MIME type + suggested filename for downloading the serialized output. */
  mimeType: string;
  filename: string;
  serialize: (colors: string[]) => string;
}

export const EXPORT_FORMATS: ExportFormat[] = [
  {
    id: 'css',
    label: 'CSS Variables',
    language: 'css',
    mimeType: 'text/css',
    filename: 'palette.css',
    serialize: toCssVars,
  },
  {
    id: 'tailwind',
    label: 'Tailwind',
    language: 'javascript',
    mimeType: 'text/javascript',
    filename: 'tailwind.config.js',
    serialize: toTailwind,
  },
  {
    id: 'figma',
    label: 'Figma Tokens',
    language: 'json',
    mimeType: 'application/json',
    filename: 'tokens.json',
    serialize: toFigmaTokens,
  },
  {
    id: 'json',
    label: 'Raw JSON',
    language: 'json',
    mimeType: 'application/json',
    filename: 'palette.json',
    serialize: toRawJson,
  },
];
